import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
    Bus, Plus, Calendar, Clock, MapPin, Users, 
    Pencil, Trash2, Eye, RefreshCw, Loader2, Download, FileText, FileSpreadsheet
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
];

export function ManagerTransportSection({ token, organizationName }) {
    const [loading, setLoading] = useState(true);
    const [trips, setTrips] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [tripModal, setTripModal] = useState({ open: false, data: null });
    const [bookingsModal, setBookingsModal] = useState({ open: false, trip: null, bookings: [] });
    const [deleteModal, setDeleteModal] = useState({ open: false, trip: null });
    const [actionLoading, setActionLoading] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchTrips = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/manager/transport/trips`, { headers });
            setTrips(response.data);
        } catch (error) {
            console.error('Error fetching trips:', error);
            toast.error('Failed to load trips');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchTrips();
    }, [fetchTrips]);

    const handleSaveTrip = async () => {
        const data = tripModal.data;
        if (!data.title || !data.destination || !data.pickup_location || !data.date || !data.departure_time) {
            toast.error('Please fill in all required fields');
            return;
        }

        setActionLoading(true);
        try {
            if (data.id) {
                await axios.put(`${API}/manager/transport/trips/${data.id}`, data, { headers });
                toast.success('Trip updated');
            } else {
                await axios.post(`${API}/manager/transport/trips`, data, { headers });
                toast.success('Trip created');
            }
            setTripModal({ open: false, data: null });
            fetchTrips();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save trip');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteTrip = async () => {
        if (!deleteModal.trip) return;
        
        setActionLoading(true);
        try {
            await axios.delete(`${API}/manager/transport/trips/${deleteModal.trip.id}`, { headers });
            toast.success('Trip cancelled');
            setDeleteModal({ open: false, trip: null });
            fetchTrips();
        } catch (error) {
            toast.error('Failed to cancel trip');
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewBookings = async (trip) => {
        try {
            const response = await axios.get(`${API}/manager/transport/trips/${trip.id}/bookings`, { headers });
            setBookingsModal({ open: true, trip: response.data.trip, bookings: response.data.bookings });
        } catch (error) {
            toast.error('Failed to load bookings');
        }
    };

    const handleDownloadFile = async (tripId, format) => {
        try {
            const response = await axios.get(
                `${API}/manager/transport/trips/${tripId}/passengers/${format}`, 
                { 
                    headers,
                    responseType: 'blob'
                }
            );
            
            // Create blob and download
            const blob = new Blob([response.data], { 
                type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            
            // Extract filename from content-disposition or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `passengers_${tripId}.${format}`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
                if (filenameMatch) {
                    filename = filenameMatch[1].replace(/"/g, '');
                }
            }
            
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success(`${format.toUpperCase()} downloaded successfully`);
        } catch (error) {
            console.error('Download error:', error);
            toast.error(`Failed to download ${format.toUpperCase()}`);
        }
    };

    const handleGenerateRecurring = async (trip) => {
        try {
            await axios.post(`${API}/manager/transport/trips/${trip.id}/generate-recurring?weeks=8`, {}, { headers });
            toast.success('Generated recurring trips for next 8 weeks');
            fetchTrips();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate recurring trips');
        }
    };

    const openNewTripModal = () => {
        setTripModal({
            open: true,
            data: {
                title: '',
                description: '',
                destination: '',
                pickup_location: 'Main Entrance',
                date: new Date().toISOString().split('T')[0],
                departure_time: '10:00',
                return_time: '15:00',
                total_seats: 8,
                is_recurring: false,
                recurrence_day: null
            }
        });
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const upcomingTrips = trips.filter(t => t.is_active && new Date(t.date) >= new Date());
    const pastTrips = trips.filter(t => !t.is_active || new Date(t.date) < new Date());

    return (
        <div className="space-y-6" data-testid="manager-transport-section">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Bus className="w-6 h-6 text-teal-600" />
                        Minibus Trips
                    </h2>
                    <p className="text-muted-foreground text-sm mt-1">
                        Manage community outings and trip bookings
                    </p>
                </div>
                <Button onClick={openNewTripModal} className="bg-teal-600 hover:bg-teal-700" data-testid="add-trip-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Trip
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <>
                    {/* Upcoming Trips */}
                    <div>
                        <h3 className="font-semibold text-stone-700 mb-3">
                            Upcoming Trips ({upcomingTrips.length})
                        </h3>
                        {upcomingTrips.length === 0 ? (
                            <div className="bg-stone-100 rounded-xl p-8 text-center">
                                <Bus className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                                <p className="text-stone-500">No upcoming trips scheduled</p>
                                <Button onClick={openNewTripModal} className="mt-4" variant="outline">
                                    Schedule First Trip
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {upcomingTrips.map((trip) => (
                                    <div 
                                        key={trip.id} 
                                        className="bg-white rounded-xl p-4 border border-stone-200"
                                        data-testid={`trip-row-${trip.id}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h4 className="font-semibold text-stone-800">{trip.title}</h4>
                                                    {trip.is_recurring && (
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                                            Weekly
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                                    <span className="flex items-center gap-1 text-stone-600">
                                                        <Calendar className="w-4 h-4 text-teal-600" />
                                                        {formatDate(trip.date)}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-stone-600">
                                                        <Clock className="w-4 h-4 text-teal-600" />
                                                        {trip.departure_time}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-stone-600">
                                                        <MapPin className="w-4 h-4 text-teal-600" />
                                                        {trip.destination}
                                                    </span>
                                                    <span className="flex items-center gap-1 text-stone-600">
                                                        <Users className="w-4 h-4 text-teal-600" />
                                                        {trip.booked_seats}/{trip.total_seats} booked
                                                        {trip.waitlist_count > 0 && ` (+${trip.waitlist_count} waitlist)`}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => handleViewBookings(trip)}
                                                    data-testid={`view-bookings-${trip.id}`}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    onClick={() => setTripModal({ open: true, data: trip })}
                                                    data-testid={`edit-trip-${trip.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                {trip.is_recurring && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleGenerateRecurring(trip)}
                                                        title="Generate more recurring trips"
                                                    >
                                                        <RefreshCw className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button 
                                                    variant="ghost" 
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => setDeleteModal({ open: true, trip })}
                                                    data-testid={`delete-trip-${trip.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Past/Cancelled Trips */}
                    {pastTrips.length > 0 && (
                        <div>
                            <h3 className="font-semibold text-stone-500 mb-3">
                                Past/Cancelled Trips ({pastTrips.length})
                            </h3>
                            <div className="space-y-2 opacity-60">
                                {pastTrips.slice(0, 5).map((trip) => (
                                    <div 
                                        key={trip.id} 
                                        className="bg-stone-50 rounded-lg p-3 border border-stone-200"
                                    >
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-medium text-stone-600">{trip.title}</span>
                                            <span className="text-stone-500">{formatDate(trip.date)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Trip Modal */}
            <Dialog open={tripModal.open} onOpenChange={(open) => !open && setTripModal({ open: false, data: null })}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{tripModal.data?.id ? 'Edit Trip' : 'New Trip'}</DialogTitle>
                        <DialogDescription>
                            {tripModal.data?.id ? 'Update trip details' : 'Schedule a new minibus trip'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {tripModal.data && (
                        <div className="space-y-4">
                            <div>
                                <Label>Trip Name *</Label>
                                <Input
                                    value={tripModal.data.title}
                                    onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, title: e.target.value }}))}
                                    placeholder="e.g., Tea & Cake at Berkhamsted"
                                    data-testid="trip-title-input"
                                />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Textarea
                                    value={tripModal.data.description}
                                    onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, description: e.target.value }}))}
                                    placeholder="Brief description of the outing..."
                                    rows={2}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label>Pickup Location *</Label>
                                    <Input
                                        value={tripModal.data.pickup_location}
                                        onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, pickup_location: e.target.value }}))}
                                        placeholder="Main Entrance"
                                        data-testid="trip-pickup-input"
                                    />
                                </div>
                                <div>
                                    <Label>Destination *</Label>
                                    <Input
                                        value={tripModal.data.destination}
                                        onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, destination: e.target.value }}))}
                                        placeholder="Berkhamsted Town Centre"
                                        data-testid="trip-destination-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label>Date *</Label>
                                    <Input
                                        type="date"
                                        value={tripModal.data.date}
                                        onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, date: e.target.value }}))}
                                        data-testid="trip-date-input"
                                    />
                                </div>
                                <div>
                                    <Label>Departure Time *</Label>
                                    <Input
                                        type="time"
                                        value={tripModal.data.departure_time}
                                        onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, departure_time: e.target.value }}))}
                                        data-testid="trip-time-input"
                                    />
                                </div>
                                <div>
                                    <Label>Return Time</Label>
                                    <Input
                                        type="time"
                                        value={tripModal.data.return_time || ''}
                                        onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, return_time: e.target.value }}))}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Number of Seats</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={tripModal.data.total_seats}
                                    onChange={(e) => setTripModal(prev => ({ ...prev, data: { ...prev.data, total_seats: parseInt(e.target.value) || 8 }}))}
                                    data-testid="trip-seats-input"
                                />
                            </div>
                            
                            {/* Recurring Options */}
                            <div className="border-t pt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Switch
                                        checked={tripModal.data.is_recurring}
                                        onCheckedChange={(checked) => setTripModal(prev => ({ 
                                            ...prev, 
                                            data: { 
                                                ...prev.data, 
                                                is_recurring: checked,
                                                recurrence_day: checked ? new Date(prev.data.date).getDay() : null
                                            }
                                        }))}
                                        data-testid="trip-recurring-toggle"
                                    />
                                    <Label>Make this a weekly recurring trip</Label>
                                </div>
                                
                                {tripModal.data.is_recurring && (
                                    <div>
                                        <Label>Repeats every</Label>
                                        <select
                                            value={tripModal.data.recurrence_day ?? ''}
                                            onChange={(e) => setTripModal(prev => ({ 
                                                ...prev, 
                                                data: { ...prev.data, recurrence_day: parseInt(e.target.value) }
                                            }))}
                                            className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg"
                                        >
                                            {DAYS_OF_WEEK.map(day => (
                                                <option key={day.value} value={day.value}>{day.label}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            This will auto-generate trips for the next 8 weeks
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTripModal({ open: false, data: null })}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSaveTrip} 
                            disabled={actionLoading}
                            className="bg-teal-600 hover:bg-teal-700"
                            data-testid="save-trip-btn"
                        >
                            {actionLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : tripModal.data?.id ? 'Update Trip' : 'Create Trip'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bookings Modal */}
            <Dialog open={bookingsModal.open} onOpenChange={(open) => !open && setBookingsModal({ open: false, trip: null, bookings: [] })}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Trip Bookings</DialogTitle>
                        {bookingsModal.trip && (
                            <DialogDescription>
                                {bookingsModal.trip.title} - {formatDate(bookingsModal.trip.date)}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    
                    {bookingsModal.trip && (
                        <div className="space-y-4">
                            <div className="flex gap-3">
                                <div className="flex-1 bg-green-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-green-700">
                                        {bookingsModal.bookings.filter(b => b.status === 'confirmed').length}
                                    </p>
                                    <p className="text-xs text-green-600">Confirmed</p>
                                </div>
                                <div className="flex-1 bg-amber-50 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-amber-700">
                                        {bookingsModal.bookings.filter(b => b.status === 'waitlisted').length}
                                    </p>
                                    <p className="text-xs text-amber-600">Waitlist</p>
                                </div>
                                <div className="flex-1 bg-stone-100 rounded-lg p-3 text-center">
                                    <p className="text-2xl font-bold text-stone-600">
                                        {bookingsModal.trip.total_seats - bookingsModal.trip.booked_seats}
                                    </p>
                                    <p className="text-xs text-stone-500">Available</p>
                                </div>
                            </div>
                            
                            <div className="border-t pt-4">
                                <h4 className="font-semibold text-stone-700 mb-3">Passengers</h4>
                                {bookingsModal.bookings.length === 0 ? (
                                    <p className="text-stone-500 text-center py-4">No bookings yet</p>
                                ) : (
                                    <div className="space-y-2">
                                        {bookingsModal.bookings
                                            .filter(b => b.status !== 'cancelled')
                                            .map((booking, idx) => (
                                                <div key={booking.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                                                    <div>
                                                        <p className="font-medium text-stone-800">{booking.user_name}</p>
                                                        <p className="text-xs text-stone-500">{booking.user_email}</p>
                                                    </div>
                                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                                        booking.status === 'confirmed' 
                                                            ? 'bg-green-100 text-green-700' 
                                                            : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                        {booking.status === 'confirmed' ? `Seat ${idx + 1}` : `Waitlist #${booking.waitlist_position}`}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDownloadFile(bookingsModal.trip?.id, 'pdf')}
                                disabled={!bookingsModal.bookings?.length}
                                data-testid="download-pdf-btn"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                PDF
                            </Button>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleDownloadFile(bookingsModal.trip?.id, 'csv')}
                                disabled={!bookingsModal.bookings?.length}
                                data-testid="download-csv-btn"
                            >
                                <FileSpreadsheet className="w-4 h-4 mr-2" />
                                CSV
                            </Button>
                        </div>
                        <Button variant="outline" onClick={() => setBookingsModal({ open: false, trip: null, bookings: [] })}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, trip: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cancel Trip</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to cancel this trip?
                        </DialogDescription>
                    </DialogHeader>
                    
                    {deleteModal.trip && (
                        <div className="bg-stone-50 rounded-xl p-4">
                            <h3 className="font-semibold text-stone-800">{deleteModal.trip.title}</h3>
                            <p className="text-sm text-stone-600 mt-1">
                                {formatDate(deleteModal.trip.date)} at {deleteModal.trip.departure_time}
                            </p>
                            {deleteModal.trip.booked_seats > 0 && (
                                <p className="text-sm text-red-600 mt-2">
                                    ⚠️ {deleteModal.trip.booked_seats} passengers have booked this trip
                                </p>
                            )}
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteModal({ open: false, trip: null })}>
                            Keep Trip
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={handleDeleteTrip}
                            disabled={actionLoading}
                            data-testid="confirm-delete-trip-btn"
                        >
                            {actionLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Cancelling...
                                </>
                            ) : 'Cancel Trip'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ManagerTransportSection;
